import { XIcon } from '@phosphor-icons/react'
import * as Dialog from '@radix-ui/react-dialog'
import * as Tabs from '@radix-ui/react-tabs'
import { useRef } from 'react'
import { ScrollableTabBar } from '../scrollable-tab-bar/scrollable-tab-bar.tsx'
import { OutcomeIcon } from './outcome-icon.tsx'
import { OutcomeReferenceTabSchema, type OutcomeReferenceTab, type OutcomeType } from './outcome-types.ts'
import { getOutcomeLabel } from './outcome-utils.ts'
import * as styles from './outcomes-reference-drawer.css.ts'

export type OutcomesReferenceDrawerProps = {
  open: boolean
  currentTab: OutcomeReferenceTab
  onTabChange: (tab: OutcomeReferenceTab) => unknown
  onClose: () => unknown
}

export const OutcomesReferenceDrawer: React.FC<OutcomesReferenceDrawerProps> = ({
  open,
  currentTab,
  onTabChange,
  onClose,
}) => {
  const tabRefs = {
    triage: useRef<HTMLButtonElement>(null),
    fix: useRef<HTMLButtonElement>(null),
    sca: useRef<HTMLButtonElement>(null),
  }

  if (!open) {
    return null
  }

  return (
    <Dialog.Root
      open
      onOpenChange={isOpen => {
        if (!isOpen) {
          onClose()
        }
      }}
      modal
    >
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content
          className={styles.content}
          onOpenAutoFocus={event => {
            event.preventDefault()
            tabRefs[currentTab].current?.focus()
          }}
        >
          <div className={styles.titleRow}>
            <Dialog.Title className={styles.title}>A guide to analysis outcomes</Dialog.Title>
            <Dialog.Description className={styles.intro} style={{ display: 'none' }}>
              Reference of all outcomes that Pixee analysis surfaces, grouped by analysis type.
            </Dialog.Description>
            <button type="button" aria-label="Close" className={styles.closeButton} onClick={onClose}>
              <XIcon className={styles.closeIcon} weight="bold" />
            </button>
          </div>
          <Tabs.Root
            className={styles.tabsRoot}
            value={currentTab}
            onValueChange={value => {
              const result = OutcomeReferenceTabSchema.safeParse(value)
              if (result.success) {
                onTabChange(result.data)
              }
            }}
            orientation="horizontal"
          >
            <ScrollableTabBar className={styles.tabRow}>
              <Tabs.List className={styles.tabList}>
                <Tabs.Trigger ref={tabRefs.triage} value="triage" className={styles.tabTrigger}>
                  Triage
                </Tabs.Trigger>
                <Tabs.Trigger ref={tabRefs.fix} value="fix" className={styles.tabTrigger}>
                  Fix
                </Tabs.Trigger>
                <Tabs.Trigger ref={tabRefs.sca} value="sca" className={styles.tabTrigger}>
                  SCA
                </Tabs.Trigger>
              </Tabs.List>
            </ScrollableTabBar>
            <Tabs.Content value="triage" className={styles.tabContent}>
              <TriageOutcomesContent />
            </Tabs.Content>
            <Tabs.Content value="fix" className={styles.tabContent}>
              <FixOutcomesContent />
            </Tabs.Content>
            <Tabs.Content value="sca" className={styles.tabContent}>
              <ScaOutcomesContent />
            </Tabs.Content>
          </Tabs.Root>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}

const TriageOutcomesContent: React.FC = () => (
  <>
    <p className={styles.intro}>
      Pixee Triage outcomes are framed as classifications, and have the following meanings:
    </p>
    <ul className={styles.outcomeList}>
      <OutcomeReferenceRow outcomeType="true-positive">
        Pixee can validate the finding as a legitimate issue that warrants remediation
      </OutcomeReferenceRow>
      <OutcomeReferenceRow outcomeType="likely-true-positive">
        Pixee suspects a <strong>true</strong> positive, given the <strong>absence</strong> of typical false positive
        conditions or security controls
      </OutcomeReferenceRow>
      <OutcomeReferenceRow outcomeType="inconclusive">
        Pixee was not able to reach a firm conclusion
      </OutcomeReferenceRow>
      <OutcomeReferenceRow outcomeType="no-fix-necessary-false-positive">
        Pixee believes the finding to be a false positive
      </OutcomeReferenceRow>
      <OutcomeReferenceRow outcomeType="no-fix-necessary-wont-fix">
        The finding is considered negligible, in that the vast majority of teams would choose to accept any potential
        risk
      </OutcomeReferenceRow>
      <OutcomeReferenceRow outcomeType="blocked">
        A manual review is required; specific reasoning and guidance will be provided as available per finding
      </OutcomeReferenceRow>
      <OutcomeReferenceRow outcomeType="error">
        A timeout or error occurred during analysis; specific reasoning and guidance will be provided as available per
        finding
      </OutcomeReferenceRow>
      <OutcomeReferenceRow outcomeType="excluded">
        The finding&apos;s rule type has been configured as out-of-scope for triage analysis
      </OutcomeReferenceRow>
    </ul>
  </>
)

const FixOutcomesContent: React.FC = () => (
  <>
    <p className={styles.intro}>Pixee Fix outcomes have the following meanings:</p>
    <ul className={styles.outcomeList}>
      <OutcomeReferenceRow outcomeType="no-fix-necessary">
        Triage determined the finding to be either a false positive or negligible, and does <strong>not</strong> warrant
        remediation
      </OutcomeReferenceRow>
      <OutcomeReferenceRow outcomeType="blocked">
        A manual review is required; specific reasoning and guidance will be provided as available per finding
      </OutcomeReferenceRow>
      <OutcomeReferenceRow outcomeType="error">
        A timeout or error occurred during analysis; specific reasoning and guidance will be provided as available per
        finding
      </OutcomeReferenceRow>
      <OutcomeReferenceRow outcomeType="excluded">
        The finding&apos;s rule type has been configured as out-of-scope for automated fixes
      </OutcomeReferenceRow>
    </ul>
  </>
)

const ScaOutcomesContent: React.FC = () => (
  <>
    <p className={styles.intro}>Pixee SCA outcomes have the following meanings:</p>
    <ul className={styles.outcomeList}>
      <OutcomeReferenceRow outcomeType="exploitable">
        Pixee can validate the vulnerability as exploitable in your code
      </OutcomeReferenceRow>
      <OutcomeReferenceRow outcomeType="no-fix-necessary-not-exploitable">
        Pixee can confirm the vulnerable code path is not reachable or exploitable
      </OutcomeReferenceRow>
      <OutcomeReferenceRow outcomeType="potentially-exploitable">
        Pixee was not able to reach a firm conclusion
      </OutcomeReferenceRow>
      <OutcomeReferenceRow outcomeType="blocked">
        A manual review is required; specific reasoning and guidance will be provided as available per finding
      </OutcomeReferenceRow>
      <OutcomeReferenceRow outcomeType="error">
        A timeout or error occurred during analysis; specific reasoning and guidance will be provided as available per
        finding
      </OutcomeReferenceRow>
      <OutcomeReferenceRow outcomeType="excluded">
        The finding&apos;s rule type has been configured as out-of-scope for SCA analysis
      </OutcomeReferenceRow>
    </ul>
  </>
)

type OutcomeReferenceRowProps = {
  outcomeType: OutcomeType
  children: React.ReactNode
}

const OutcomeReferenceRow: React.FC<OutcomeReferenceRowProps> = ({ outcomeType, children }) => (
  <li className={styles.outcomeRow}>
    <span className={styles.outcomeIconWrapper}>
      <OutcomeIcon outcomeType={outcomeType} size="default" />
    </span>
    <div className={styles.outcomeText}>
      <p className={styles.outcomeName}>{getOutcomeLabel(outcomeType)}</p>
      <p className={styles.outcomeDescription}>{children}</p>
    </div>
  </li>
)
